<template>
  <div>
    <slot></slot>
  </div>
</template>
<script lang="ts">
import {
  Component,
  Emit,
  Prop,
  Provide,
  Vue,
  Watch
} from "vue-facing-decorator";
import { inject } from "vue";

interface CatalystApiOptions {
  preUpdate(json: any): any;
  updateUrl(appCode: string, id: string): String;
  uploadUrl(appCode: string, id: string): String;
  dataUrl(appCode: string, id: string): String;
  lookupUrl(appCode: string, id: string, nodeid: string): String;
}

interface CatalystApi {
  new (appCode: string, id: string, options: CatalystApiOptions);

  processUpdate(options: any): Promise<any>;
  getData(options: any): Promise<any>;
  processUpload(options: any): Promise<any>;
  processLookup(options: any): Promise<any>;
  processDownload(options: any): Promise<any>;

  stateConsumers: any;
  flashConsumers: any;
  serviceStateConsumers: any;
}

@Component({ name: "api-provider" })
export default class ApiProvider extends Vue {
  @Prop({ type: String, default: undefined })
  public readonly appCode!: string;

  @Prop({
    type: String,
    default: "glue",
    description: "Identifier for the API consumers"
  } as any)
  public readonly consumerKey!: string;

  @Prop()
  public readonly id!: string;

  @Prop()
  public readonly urlProvider!: CatalystApiOptions;

  public api!: CatalystApi;
  public apiOpts!: CatalystApiOptions;

  created() {
    const factory = (window as any).CatApi as CatalystApi;
    this.presentationMode = "showChanges";

    this.apiOpts = {
      ...({
        preUpdate: this.preFlight,
        updateUrl: (appCode: string, id: string) =>
          "update/" + appCode + "/" + id,
        uploadUrl: (appCode: string, id: string) =>
          "upload/" + appCode + "/" + id,
        dataUrl: (appCode: string, id: string) =>
          "update/" + appCode + "/" + id,
        lookupUrl: (appCode: string, id: string, nodeid: string) =>
          "lookup/" + appCode + "/" + id + "/" + nodeid
      } as CatalystApiOptions),
      ...this.urlProvider
    };

    this.api = new factory(this.appCode, this.id, this.apiOpts);
    this.api.stateConsumers[this.consumerKey] = this.emitState;
    this.api.flashConsumers[this.consumerKey] = this.setFlash;
    this.api.serviceStateConsumers[this.consumerKey] = this.emitServiceState;
  }

  setFlash(flash: any): void {
    (window as any).serviceVue["flash"] = flash;
  }

  @Watch("id")
  updateId(id: string): void {
    const factory = (window as any).CatApi as CatalystApi;
    this.api = new factory(this.appCode, this.id, this.apiOpts);
    this.api.stateConsumers[this.consumerKey] = this.emitState;
  }

  @Emit()
  preFlight(apiRequest: any): any {
    return apiRequest;
  }

  @Emit()
  emitState(state: any): any {
    return { state: state };
  }

  @Emit()
  emitServiceState(serviceState: any): any {
    return { serviceState: serviceState };
  }

  @Provide()
  catalystApi(): CatalystApi {
    return this.api;
  }

  @Provide()
  presentationMode: "showChanges";

  @Provide()
  resolveTxt = inject("resolveTxt", (key: string, def: string) => {
    return def;
  });

  @Provide()
  suppressExternalHelp = inject("suppressExternalHelp", true);
}
</script>
